import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';

interface BookedProduct {
  product_id: string;
  product_type: string;
  date_debut: string;
  date_fin: string;
}

/**
 * Hook to check product availability for given dates.
 * Counts reservations with status 'loue' that overlap the selected dates
 * and compares against the product's available quantity.
 */
export function useAvailability(dateDebut?: string, dateFin?: string) {
  const [bookedProducts, setBookedProducts] = useState<BookedProduct[]>([]);
  const [productQuantities, setProductQuantities] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);

  const checkAvailability = useCallback(async () => {
    if (!dateDebut || !dateFin) {
      setBookedProducts([]);
      return;
    }

    try {
      setLoading(true);

      // Fetch overlapping confirmed reservations and chateau quantities in parallel
      const [reservationsRes, chateauxRes] = await Promise.all([
        supabase
          .from('reservations' as any)
          .select('product_id, product_type, date_debut, date_fin')
          .eq('status', 'loue')
          .lte('date_debut', dateFin)
          .gte('date_fin', dateDebut),
        supabase
          .from('chateaux')
          .select('id, quantite')
      ]);

      if (reservationsRes.error) throw reservationsRes.error;
      setBookedProducts((reservationsRes.data as any[]) || []);

      // Build quantity map
      if (!chateauxRes.error && chateauxRes.data) {
        const qMap: Record<string, number> = {};
        for (const c of chateauxRes.data) {
          qMap[c.id] = (c as any).quantite ?? 1;
        }
        setProductQuantities(qMap);
      }
    } catch (error) {
      logger.error('Erreur vérification disponibilité', error);
      setBookedProducts([]);
    } finally {
      setLoading(false);
    }
  }, [dateDebut, dateFin]);

  useEffect(() => {
    checkAvailability();
  }, [checkAvailability]);

  const isProductAvailable = useCallback((productId: string, productType: string = 'chateau') => {
    if (!dateDebut || !dateFin) return true;

    // Count how many confirmed bookings overlap for this product
    const bookedCount = bookedProducts.filter(
      b => b.product_id === productId && b.product_type === productType
    ).length;

    // Get available quantity (default 1)
    const totalQuantity = productQuantities[productId] ?? 1;

    return bookedCount < totalQuantity;
  }, [bookedProducts, productQuantities, dateDebut, dateFin]);

  const isSumoAvailable = useCallback(() => {
    if (!dateDebut || !dateFin) return true;
    return !bookedProducts.some(b => b.product_type === 'sumo');
  }, [bookedProducts, dateDebut, dateFin]);

  return {
    bookedProducts,
    loading,
    isProductAvailable,
    isSumoAvailable,
    checkAvailability,
    hasDatesSelected: Boolean(dateDebut && dateFin),
  };
}
